import { Chip, Tooltip } from "@mui/material";
import MoneyRequestActionButtons from "../../../components/Fleet/MoneyRequestActionButtons/MoneyRequestActionButtons";
import DownloadButton from "../../../components/Fleet/DownloadButton/DownloadButton";
import { camelCaseToSentenceCase } from "../../../helpers/camelCaseToSentenceCase";
import { formatDate } from "../../../helpers/formatingFunctions";
import UpdateMoneyRequestPopup from "../../../components/Fleet/UpdateMoneyRequestPopup/UpdateMoneyRequestPopup";
import AddInMcleodButton from "../../../components/Fleet/AddInMcleodButton/AddInMcleodButton";
import ConfirmActionButton from "../../../components/Fleet/ConfirmActionButton/ConfirmActionButton";

export type MoneyRequestStatusType =
  | "pending"
  | "approved"
  | "declined"
  | "completedWithoutApproval";
export const statusColors: Record<
  MoneyRequestStatusType,
  {
    label: string;
    color:
      | "default"
      | "primary"
      | "secondary"
      | "error"
      | "info"
      | "success"
      | "warning";
  }
> = {
  approved: { label: "Approved", color: "success" },
  pending: { label: "Pending", color: "warning" },
  declined: { label: "Declined", color: "error" },
  completedWithoutApproval: {
    label: "Completed Without Approval",
    color: "secondary",
  },
};

export const moneyRequestsTableColumns = [
  {
    field: "isAddedInMcleod",
    headerName: "In McLeod",
    width: 130,
    renderCell: (params: any) => (
      <AddInMcleodButton id={params.row.id} moneyRequest={params.row} />
    ),
  },
  { field: "driverName", headerName: "Driver Name", width: 150 },
  { field: "unitNumber", headerName: "Unit Number", width: 90 },
  { field: "nameRequestedBy", headerName: "Requested By", width: 150 },
  {
    field: "dateRequested",
    headerName: "Date Requested",
    width: 150,
    valueFormatter: (value: string) => formatDate(value),
  },
  {
    field: "amount",
    headerName: "Amount",
    width: 90,
    valueFormatter: (value: string) => {
      return `$${value}`;
    },
  },
  {
    field: "moneyRequestType",
    headerName: "Request For",
    width: 150,
    valueFormatter: (value: string) => {
      return camelCaseToSentenceCase(value);
    },
  },
  {
    field: "paymentMethod",
    headerName: "Payment Method",
    width: 150,
    valueFormatter: (value: string) => {
      return camelCaseToSentenceCase(value).toUpperCase();
    },
  },
  {
    field: "referenceId",
    headerName: "Reference ID",
    width: 150,
    valueFormatter: (value: string) => {
      return value;
    },
  },
  {
    field: "efsAccount",
    headerName: "EFS Account",
    width: 130,
    valueGetter: (value: string | null, row: any) =>
      row.paymentMethod?.toLowerCase?.() === "efs" ? value : null,
    valueFormatter: (value: string | null) => value || "—",
  },
  { field: "description", headerName: "Description", width: 300 },
  { field: "issuedFor", headerName: "Issued For", width: 150 },
  {
    field: "status",
    headerName: "Status",
    width: 150,
    renderCell: (params: any) => (
      <Tooltip
        title={
          params.value === "approved" && params.row.nameApprovedBy
            ? `Approved by ${params.row.nameApprovedBy} on ${new Date(
                params.row.dateApproved
              ).toLocaleDateString()}
                  ${
                    params.row.approvalNotes !== ""
                      ? `| Notes: ${params.row.approvalNotes}`
                      : ""
                  }`
            : params.value === "declined" && params.row.nameDeclinedBy
            ? `Declined by ${params.row.nameDeclinedBy} on ${new Date(
                params.row.dateDeclined
              ).toLocaleDateString()}
                  ${
                    params.row.declineNotes !== ""
                      ? `| Notes: ${params.row.declineNotes}`
                      : ""
                  }`
            : params.value === "completedWithoutApproval"
            ? `Requested on ${new Date(
                params.row.dateRequested
              ).toLocaleDateString()}
                  ${
                    params.row.reason !== ""
                      ? `| Reason: ${params.row.reason}`
                      : ""
                  }`
            : params.value === "pending"
            ? `Requested on ${new Date(
                params.row.dateRequested
              ).toLocaleDateString()}`
            : "No need for approval"
        }
      >
        <Chip
          label={statusColors[params.value as MoneyRequestStatusType].label}
          color={statusColors[params.value as MoneyRequestStatusType].color}
        />
      </Tooltip>
    ),
  },
  {
    field: "actions",
    headerName: "",
    width: 500,
    renderCell: (params: any) => <MoneyRequestActionButtons params={params} />,
  },
  {
    field: "downloadInvoice",
    headerName: "Download Invoice",
    width: 194,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params: any) =>
      (params.row.filesAvailability === "invoice" ||
        params.row.filesAvailability === "both") && (
        <DownloadButton params={params} type="moneyRequestInvoice" />
      ),
  },
  {
    field: "downloadReceipt",
    headerName: "Download Receipt",
    width: 196,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params: any) =>
      (params.row.filesAvailability === "receipt" ||
        params.row.filesAvailability === "both") && (
        <DownloadButton params={params} type="moneyRequestReceipt" />
      ),
  },
  {
    field: "update",
    headerName: "",
    width: 130,
    renderCell: (params: any) => (
      <UpdateMoneyRequestPopup id={params.row.id} moneyRequest={params.row} />
    ),
  },
  {
    field: "delete",
    headerName: "",
    width: 130,
    renderCell: (params: any) => (
      <ConfirmActionButton
        id={params.row.id}
        entityType="MoneyRequest"
        actionType="delete"
        entityLabel="money request"
        queryKey="moneyRequests"
        buttonLabel="Delete"
        buttonColor="error"
      />
    ),
  },
];
